// pages/Ec2TopTab.jsx — EC2 Health → instance modal → "Processes" tab (admin only, Linux)
// Backend: POST /api/ec2-health/top (backend/ec2Top.js) — read-only `top -b -n2` snapshot via SSH / SSM.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../context/AuthContext';

const INTERVALS = [[0, 'Off'], [10, '10 s'], [30, '30 s'], [60, '1 min']];
const VIA = { ssm: 'SSM Run Command', ssh: 'SSH' };
const STATE = { R: 'running', S: 'sleeping', D: 'disk wait', Z: 'zombie', T: 'stopped', I: 'idle' };

function fmtKiB(k) {
  if (k == null) return '—';
  if (k >= 1024 * 1024) return (k / 1024 / 1024).toFixed(1) + ' G';
  if (k >= 1024) return (k / 1024).toFixed(0) + ' M';
  return k + ' K';
}
// top TIME+ "mm:ss.hh" atau "hhh:mm" → detik (kira-kira, cukup untuk sort)
function timeSec(t) {
  if (!t) return null;
  const p = String(t).split(':').map(parseFloat);
  if (p.some(isNaN)) return null;
  return p.length === 2 ? (String(t).includes('.') ? p[0] * 60 + p[1] : p[0] * 3600 + p[1] * 60) : p[0];
}
const COLS = [
  { h: 'PID', num: true, get: (p) => +p.pid },
  { h: 'User', get: (p) => p.user },
  { h: 'S', get: (p) => p.state },
  { h: '%CPU', num: true, get: (p) => p.cpu },
  { h: '%MEM', num: true, get: (p) => p.mem },
  { h: 'RES', num: true, get: (p) => p.res },
  { h: 'VIRT', num: true, get: (p) => p.virt },
  { h: 'Time+', num: true, get: (p) => timeSec(p.time) },
  { h: 'Command', get: (p) => p.cmd },
];
const pctCls = (v, w, c) => (v >= c ? 'text-red-600 font-semibold' : v >= w ? 'text-amber-600 font-semibold' : 'text-gray-800');

function Bar({ pct, cls }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
      <div className={`h-full ${cls}`} style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} />
    </div>
  );
}

function Card({ label, value, sub, pct }) {
  const cls = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-sky-500';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums text-gray-900">{value}</div>
      {pct != null && <div className="mt-1"><Bar pct={pct} cls={cls} /></div>}
      {sub && <div className="mt-1 text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
}

export default function Ec2TopTab({ x, account, sshTarget }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState('cpu');
  const [every, setEvery] = useState(0);
  const [q, setQ] = useState('');
  const [wide, setWide] = useState(false);
  const [col, setCol] = useState(null); // { h, dir } — klik header kolom; null = urutan bawaan top
  const reqId = useRef(0);
  const busyRef = useRef(false);

  const ok = !x.windows && x.state === 'running' && (!!sshTarget || x.ssmPing === 'Online');
  const why = x.windows ? 'Processes view is Linux only for now.'
    : x.state !== 'running' ? `Instance is ${x.state}.`
      : `No SSH target for ${x.privateIp || 'this IP'} and SSM agent ${x.ssmPing || 'not registered'}.`;

  const load = useCallback(async () => {
    if (!ok || busyRef.current) return;
    const my = ++reqId.current;
    busyRef.current = true; setBusy(true);
    try {
      const r = await api.post('/api/ec2-health/top', { account, instanceId: x.id });
      if (my === reqId.current) { setData(r.data); setErr(null); }
    } catch (e) {
      if (my === reqId.current) setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally {
      busyRef.current = false;
      if (my === reqId.current) setBusy(false);
    }
  }, [ok, account, x.id]);

  useEffect(() => { reqId.current++; busyRef.current = false; setData(null); setErr(null); setBusy(false); setEvery(0); }, [x.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!every) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(); }, every * 1000);
    return () => clearInterval(t);
  }, [every, load]);

  if (!ok) return <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600">{why}</div>;

  const s = (data && data.summary) || {};
  const mem = s.mem || {};
  const swap = s.swap || {};
  const memUsedK = mem.total ? (mem.avail != null ? mem.total - mem.avail : mem.total - (mem.free || 0) - (mem.buffers || 0) - (mem.cached || 0) - (mem.buffCache || 0)) : null;
  const memUsedPct = mem.total ? Math.round((memUsedK / mem.total) * 1000) / 10 : null;
  const swapPct = swap.total ? Math.round((swap.used / swap.total) * 1000) / 10 : null;
  const loadPct = s.load && s.nproc ? Math.round((s.load[0] / s.nproc) * 100) : null;
  const rows = ((data && (sort === 'mem' ? data.byMem : data.byCpu)) || [])
    .filter((p) => !q || (p.cmd + ' ' + p.user + ' ' + p.pid).toLowerCase().includes(q.toLowerCase()));
  if (col) {
    const k = COLS.find((c) => c.h === col.h);
    const get = k.get;
    rows.sort((a, b) => {
      const va = get(a), vb = get(b);
      const r = typeof va === 'string' || typeof vb === 'string'
        ? String(va ?? '').localeCompare(String(vb ?? ''))
        : (va ?? -Infinity) - (vb ?? -Infinity);
      return col.dir === 'asc' ? r : -r;
    });
  }
  const clickHead = (h) => {
    if (h === '%CPU' && sort !== 'cpu') setSort('cpu');
    if (h === '%MEM' && sort !== 'mem') setSort('mem');
    setCol((c) => (c && c.h === h ? (c.dir === 'desc' ? { h, dir: 'asc' } : null) : { h, dir: COLS.find((x2) => x2.h === h).num ? 'desc' : 'asc' }));
  };
  const cpuMax = 100; // top %CPU is per-core (can exceed 100 on multi-core)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={load} disabled={busy}
          className="rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          {busy ? 'Loading…' : 'Refresh'}
        </button>
        <label className="flex items-center gap-1 text-xs text-gray-600">Auto-refresh
          <select value={every} onChange={(e) => setEvery(+e.target.value)} className="rounded-md border border-gray-300 px-1.5 py-1 text-xs">
            {INTERVALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <div className="flex overflow-hidden rounded-lg border border-gray-300 text-xs">
          {[['cpu', 'Sort by CPU'], ['mem', 'Sort by memory']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setSort(k); setCol(null); }}
              className={`px-2.5 py-1 ${sort === k ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{l}</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter command / user / PID"
          className="min-w-[180px] flex-1 rounded-lg border border-gray-300 px-2.5 py-1 text-xs" />
        <label className="flex items-center gap-1 text-xs text-gray-600">
          <input type="checkbox" checked={wide} onChange={(e) => setWide(e.target.checked)} /> Full command
        </label>
      </div>
      {data && (
        <div className="text-[11px] text-gray-500">
          {s.time ? `Host time ${s.time}` : ''}{s.uptime ? ` · up ${s.uptime}` : ''}{s.users != null ? ` · ${s.users} user(s)` : ''}
          {' · '}via {VIA[data.via] || data.via} · {(data.ms / 1000).toFixed(1)} s · fetched {new Date(data.fetchedAt).toLocaleTimeString()}
          {every ? ` · auto every ${every} s` : ''}
        </div>
      )}
      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {!data && !err && <div className="text-sm text-gray-500">Running top on the instance…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Card label="CPU (1 s)" value={s.cpu && s.cpu.used != null ? `${s.cpu.used}%` : '—'} pct={s.cpu && s.cpu.used}
              sub={s.cpu ? `us ${s.cpu.us} · sy ${s.cpu.sy} · ni ${s.cpu.ni} · wa ${s.cpu.wa} · st ${s.cpu.st}` : null} />
            <Card label="Load average" value={s.load ? s.load.map((v) => v.toFixed(2)).join('  ') : '—'} pct={loadPct}
              sub={s.nproc ? `1 / 5 / 15 min · ${s.nproc} vCPU (${loadPct}% of cores)` : '1 / 5 / 15 min'} />
            <Card label="Memory" value={memUsedPct != null ? `${memUsedPct}%` : '—'} pct={memUsedPct}
              sub={mem.total ? `${fmtKiB(memUsedK)} used (excl. cache) of ${fmtKiB(mem.total)}` : null} />
            <Card label="Tasks / swap" value={s.tasks ? `${s.tasks.total} tasks` : '—'} pct={swapPct}
              sub={`${s.tasks ? `${s.tasks.running} run · ${s.tasks.sleeping} sleep · ${s.tasks.zombie} zombie` : ''}${swap.total ? ` · swap ${fmtKiB(swap.used)} / ${fmtKiB(swap.total)}` : ''}`} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  {COLS.map(({ h }) => (
                    <th key={h} onClick={() => clickHead(h)} title="Klik untuk sort (desc → asc → default)"
                      className={`cursor-pointer select-none whitespace-nowrap px-2 py-1.5 hover:text-gray-800 ${col && col.h === h ? 'text-sky-700' : ''} ${['%CPU', '%MEM', 'RES', 'VIRT', 'PID'].includes(h) ? 'text-right' : ''}`}>
                      {h}{col && col.h === h ? (col.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-mono">
                {rows.map((p) => (
                  <tr key={p.pid} className="hover:bg-sky-50/50">
                    <td className="px-2 py-1 text-right tabular-nums text-gray-500">{p.pid}</td>
                    <td className="max-w-[90px] truncate px-2 py-1">{p.user}</td>
                    <td className="px-2 py-1" title={STATE[p.state] || p.state}>{p.state}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${pctCls(p.cpu, 50, 90)}`}>
                      <div>{p.cpu != null ? p.cpu.toFixed(1) : '—'}</div>
                      <div className="mt-0.5 w-14"><Bar pct={(p.cpu / cpuMax) * 100} cls="bg-sky-500" /></div>
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums ${pctCls(p.mem, 20, 40)}`}>{p.mem != null ? p.mem.toFixed(1) : '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtKiB(p.res)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-gray-500">{fmtKiB(p.virt)}</td>
                    <td className="whitespace-nowrap px-2 py-1 tabular-nums text-gray-500">{p.time}</td>
                    <td className={`px-2 py-1 text-gray-800 ${wide ? 'break-all' : 'max-w-[420px] truncate'}`} title={p.cmd}>{p.cmd}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={9} className="px-2 py-3 text-center text-gray-500">No processes match.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-gray-400">
            Top 40 processes by {sort === 'mem' ? 'memory' : 'CPU'} from <code>top -b -n2 -d1</code> (second sample, so %CPU is over 1 second; 100% = one full vCPU). Read-only.
          </div>
        </>
      )}
    </div>
  );
}
